/**
 * Client copy — keep in sync with utils/prMunicipalityShared.js
 * Shared PR municipio normalization (CommonJS for Node + Webpack).
 * @param {object} data - client/src/app/data/pr-municipalities.json
 */
function buildPrMunicipalityApi(data) {
  const municipalities = data.municipalities || [];
  const pdfLabels = data.pdfLabels || {};
  const aliasesRaw = data.aliases || {};

  const municipalitySet = new Set(municipalities);

  /** folded lowercase single-space key -> canonical Title Case ASCII */
  const canonicalByFolded = new Map();
  municipalities.forEach((m) => {
    const key = foldAndLowerKey(m);
    canonicalByFolded.set(key, m);
  });

  const aliasToCanonical = {};
  Object.keys(aliasesRaw).forEach((k) => {
    const key = foldAndLowerKey(k);
    const canonical = aliasesRaw[k];
    if (canonical && municipalitySet.has(canonical)) {
      aliasToCanonical[key] = canonical;
    }
  });

  function foldDiacritics(str) {
    if (str == null) return '';
    return String(str)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function foldAndLowerKey(str) {
    return foldDiacritics(String(str))
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function collapseWhitespace(str) {
    return String(str).replace(/\s+/g, ' ').trim();
  }

  function toTitleCaseWords(str) {
    const s = foldAndLowerKey(str);
    if (!s) return '';
    return s
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /**
   * @param {string|null|undefined} value
   * @returns {{ canonical: string, isKnown: boolean }}
   */
  function normalizePrMunicipality(value) {
    if (value == null || value === '') {
      return { canonical: '', isKnown: false };
    }
    const trimmed = collapseWhitespace(value);
    if (!trimmed) {
      return { canonical: '', isKnown: false };
    }
    const foldedKey = foldAndLowerKey(trimmed);

    if (aliasToCanonical[foldedKey]) {
      return { canonical: aliasToCanonical[foldedKey], isKnown: true };
    }
    if (canonicalByFolded.has(foldedKey)) {
      return { canonical: canonicalByFolded.get(foldedKey), isKnown: true };
    }
    return { canonical: toTitleCaseWords(trimmed), isKnown: false };
  }

  function normalizePrMunicipalityField(value) {
    return normalizePrMunicipality(value).canonical;
  }

  /**
   * Display / PDF: accented label when known, else best-effort from stored value.
   * @param {string|null|undefined} value - stored DB or raw string
   * @returns {string}
   */
  function formatPrMunicipalityForPdf(value) {
    if (value == null || value === '') return '';
    const { canonical, isKnown } = normalizePrMunicipality(value);
    if (isKnown && pdfLabels[canonical]) {
      return pdfLabels[canonical];
    }
    if (canonical) return canonical;
    return String(value).trim();
  }

  const PR_MUNICIPIO_KEYS = new Set([
    'postalCity',
    'physicalCity',
    'locationCity',
    'deliveryCity',
    'personalPhysicalCity',
    'personalPostalCity',
    'partnerPhysicalCity',
    'partnerPostalCity',
    'commercialPhysicalCity',
    'commercialPostalCity',
  ]);

  /** In-place: normalize whitelisted PR municipio string fields on nested objects/arrays. */
  function normalizePrMunicipioFieldsDeep(value) {
    if (value == null) return value;
    if (Array.isArray(value)) {
      value.forEach((item) => normalizePrMunicipioFieldsDeep(item));
      return value;
    }
    if (typeof value !== 'object') return value;
    for (const k of Object.keys(value)) {
      const v = value[k];
      if (PR_MUNICIPIO_KEYS.has(k)) {
        value[k] = normalizePrMunicipalityField(v);
      } else if (v && typeof v === 'object') {
        normalizePrMunicipioFieldsDeep(v);
      }
    }
    return value;
  }

  return {
    normalizePrMunicipality,
    normalizePrMunicipalityField,
    formatPrMunicipalityForPdf,
    normalizePrMunicipioFieldsDeep,
    PR_MUNICIPALITIES_ASCII: municipalities.slice(),
    municipalitySet,
  };
}

module.exports = { buildPrMunicipalityApi };
